import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

/**
 * EmptyState
 * Centered empty/zero-data display with optional icon, description, and action.
 *
 * @param {ReactNode} icon
 * @param {string} title
 * @param {string} [description]
 * @param {string} [actionLabel]
 * @param {() => void} [onAction]
 * @param {ReactNode} [illustration]
 */
function EmptyState({ icon, title, description, actionLabel, onAction, illustration, sx = {} }) {
  return (
    <MotionBox
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.24 }}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: 6,
        px: 3,
        ...sx,
      }}
    >
      {illustration ? (
        <Box sx={{ mb: 2 }}>{illustration}</Box>
      ) : icon ? (
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: 'background.subtle',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
            color: 'text.tertiary',
            '& .MuiSvgIcon-root': { fontSize: 36 },
          }}
        >
          {icon}
        </Box>
      ) : null}

      <Typography variant="h6" fontWeight={700} color="text.primary" mb={0.75}>
        {title}
      </Typography>

      {description && (
        <Typography variant="body2" color="text.secondary" mb={2.5} maxWidth={280}>
          {description}
        </Typography>
      )}

      {actionLabel && onAction && (
        <Button variant="contained" onClick={onAction}>
          {actionLabel}
        </Button>
      )}
    </MotionBox>
  );
}

export default EmptyState;
